import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Footer } from '../Layout/Footer';

export const HomePage: React.FC = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      {/* Dashboard Button for logged in users */}
      {user && (
        <div className="absolute top-4 right-4 z-10">
          <Link to="/dashboard" className="btn btn-primary">Go to Dashboard</Link>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-white min-h-screen flex items-center font-space-grotesk relative overflow-hidden">
        {/* Subtle Background Graphics */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Geometric shapes */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full opacity-30"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full opacity-20"></div>
          <div className="absolute bottom-32 left-1/4 w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full opacity-25"></div>
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Logo Branding */}
        <div className="absolute top-8 left-8 z-20">
          <img src="/logo.png" alt="SuprHR Logo" className="h-16 md:h-24" />
        </div>

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
          
          {/* Text Content */}
          <div className="mt-32 md:mt-0 space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center px-3 py-1 bg-black text-white text-xs font-medium rounded-full">
              <span className="mr-2">✨</span> AI-Powered Resume Screening
            </div>
            
            <div>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight tracking-normal">
                Screen hundreds of resumes in minutes
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Upload job descriptions and resumes. Get instant AI-powered matching scores. 
                <span className="font-semibold text-gray-900"> No setup, no complexity.</span>
              </p>
            </div>

            {/* Feature Tags */}
            <div className="flex flex-wrap gap-3 mb-8">
              <span className="flex items-center px-4 py-2 bg-zinc-100 text-sm text-gray-700 rounded-full border border-gray-200">
                <span className="mr-2 text-red-500">✖</span> No ATS
              </span>
              <span className="flex items-center px-4 py-2 bg-zinc-100 text-sm text-gray-700 rounded-full border border-gray-200">
                <span className="mr-2 text-red-500">✖</span> No SSO
              </span>
              <span className="flex items-center px-4 py-2 bg-zinc-100 text-sm text-gray-700 rounded-full border border-gray-200">
                <span className="mr-2 text-red-500">✖</span> No Onboarding
              </span>
              <span className="flex items-center px-4 py-2 bg-zinc-100 text-sm text-gray-700 rounded-full border border-gray-200">
                <span className="mr-2 text-red-500">✖</span> No Nonsense
              </span>
            </div>

            {/* CTA Section */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/auth" className="inline-flex items-center justify-center bg-black text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl">
                Start Screening Now
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
              </Link>
              <div className="flex items-center text-sm text-gray-500">
                <span className="mr-2">⚡</span>
                <span>Free to start • No credit card required</span>
              </div>
            </div>
          </div>

          {/* Visual */}
          <div className="relative">
            {/* Video container */}
            <div className="w-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-xl border border-gray-200 relative overflow-hidden">
              <iframe 
                title="vimeo-player" 
                src="https://player.vimeo.com/video/1104480980?h=09e01d497f" 
                width="100%" 
                height="360" 
                frameBorder="0" 
                referrerPolicy="strict-origin-when-cross-origin" 
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
                allowFullScreen
                className="rounded-t-2xl"
                style={{
                  marginTop: '-1rem',
                }}
              ></iframe>
              
              {/* Text below video */}
              <div className="text-center">
                <div className="text-gray-600 text-base font-medium mb-4">See SuprHR in action</div>
              </div>
            </div>
            
            {/* Floating elements */}
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-white rounded-xl shadow-lg border border-gray-200 flex items-center justify-center">
              <div className="text-2xl">📊</div>
            </div>
            <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-white rounded-lg shadow-lg border border-gray-200 flex items-center justify-center">
              <div className="text-lg">🎯</div>
            </div>
          </div>

        </div>
      </section>

      {/* How It Works Section */}
      <div className="py-16 bg-zinc-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 font-space-grotesk">How It Works</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-black text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Upload Job Description</h3>
              <p className="text-gray-600">
                Upload your job description in any format - PDF, DOCX, or plain text.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-black text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Upload Resumes</h3>
              <p className="text-gray-600">
                Upload candidate resumes in bulk. Our system handles multiple formats.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-black text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Get Results</h3>
              <p className="text-gray-600">
                Receive detailed analysis with fitment scores and candidate insights instantly.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Value Proposition Section */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-900 font-space-grotesk">Why SuprHR?</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            While others try to do everything, we focus on one thing: making resume screening fast, accurate, and simple.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-center">
                <div className="text-4xl mb-4">⚡</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">30-Second Setup</h3>
                <p className="text-gray-600">
                  No complex onboarding. Create account, upload job description, upload resumes. Done.
                </p>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-center">
                <div className="text-4xl mb-4">🤖</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">AI-Powered Analysis</h3>
                <p className="text-gray-600">
                  Advanced AI algorithms analyze resumes against job descriptions for precise matching.
                </p>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-center">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Just Screening</h3>
                <p className="text-gray-600">
                  No ATS bloat, no unnecessary features. Pure focus on what matters: finding the right candidates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}; 